/**
 * useAnalytics — Hook RGPD-by-design pour CONFILEX.
 *
 * Principes :
 *   - Aucune librairie tierce. Pas de cookies. Tout est posté à notre backend.
 *   - anon_id stocké en localStorage UNIQUEMENT après consentement
 *     (cf. consent cookie `confilex_analytics_consent=accepted`).
 *   - Respecte DNT (Do Not Track) côté navigateur.
 *   - Si l'utilisateur est connecté, le JWT est joint via apiClient — le
 *     backend ratache automatiquement le user_id.
 *
 * Utilisation : monter <AnalyticsTracker /> une seule fois dans App.js.
 *               Les autres composants peuvent appeler `trackClick`,
 *               `trackConversion` depuis ce module.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { apiClient } from "@/lib/api";

const ANON_KEY = "confilex_anon_id";
const CONSENT_KEY = "confilex_analytics_consent"; // "accepted" | "denied"

function dntEnabled() {
  if (typeof navigator === "undefined") return false;
  // Standard "1", IE "yes", Safari "1"
  return navigator.doNotTrack === "1" || navigator.doNotTrack === "yes" || window.doNotTrack === "1";
}

function consentGranted() {
  try { return localStorage.getItem(CONSENT_KEY) === "accepted"; } catch { return false; }
}

function getOrCreateAnonId() {
  if (!consentGranted()) return null; // pas de stockage tant que pas de consentement
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch { return null; }
}

async function postEvent(payload) {
  if (dntEnabled()) return; // jamais d'envoi si DNT activé
  try {
    await apiClient.post("/analytics/event", {
      ...payload,
      anon_id: getOrCreateAnonId(),
      ts_client: new Date().toISOString(),
      referrer: payload.referrer ?? (typeof document !== "undefined" ? document.referrer : ""),
    });
  } catch {
    /* swallow — analytics ne doit jamais bloquer le parcours utilisateur */
  }
}

export function trackPageView(path) {
  return postEvent({ type: "page_view", path });
}

export function trackClick(target, label) {
  return postEvent({ type: "click", path: window.location.pathname, target, label });
}

export function trackConversion(target, extra) {
  return postEvent({ type: "conversion", path: window.location.pathname, target, extra });
}

export function trackSearch(query) {
  return postEvent({ type: "search", path: window.location.pathname, label: (query || "").slice(0, 80) });
}

/**
 * <AnalyticsTracker /> — composant invisible monté dans App.js.
 * Suit automatiquement les changements de page via React Router.
 */
export default function AnalyticsTracker() {
  const loc = useLocation();
  useEffect(() => {
    trackPageView(loc.pathname);
  }, [loc.pathname]);
  return null;
}
